import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@/components/theme-provider';

// Components
import Sidebar from './components/Sidebar';
import { TopNav } from './components/TopNav';
import { CommandPalette } from './components/CommandPalette';
import Dashboard from './pages/Dashboard';
import Licenses from './pages/Licenses';
import Users from './pages/Users';
import Products from './pages/Products';
import Policies from './pages/Policies';
import Customers from './pages/Customers';
import Devices from './pages/Devices';
import Partners from './pages/Partners';
import PurchaseOrders from './pages/PurchaseOrders';
import SystemSignatures from './pages/SystemSignatures';
import SystemLogs from './pages/SystemLogs';
import Roles from './pages/Roles';
import Organizations from './pages/Organizations';
import ApiKeys from './pages/ApiKeys';
import Activations from './pages/Activations';
import VerificationPackages from './pages/VerificationPackages';
import Analytics from './pages/Analytics';
import ActivityLogs from './pages/ActivityLogs';
import Notifications from './pages/Notifications';
import Webhooks from './pages/Webhooks';
import Domains from './pages/Domains';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import MFA from './pages/MFA';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Landing from './pages/Landing';
import Downloads from './pages/Downloads';
import SDKDocumentation from './pages/SDKDocumentation';
import SuperAdminDashboard from './pages/SuperAdminDashboard';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      // Fetch user data
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.data);
      } else {
        // Token invalid, clear it
        localStorage.removeItem('token');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token: string, userData: any) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    setSidebarOpen(false);
  };

  if (loading) {
    return (
      <ThemeProvider defaultTheme="light">
        <div className="flex h-screen items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="relative flex h-12 w-12 items-center justify-center rounded-md border bg-card">
                <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">Loading AirGap Suite</p>
              <p className="text-xs text-muted-foreground">Verifying session...</p>
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider defaultTheme="light">
      <Router>
        {!isAuthenticated ? (
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/auth/callback" element={<AuthCallback onLogin={handleLogin} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* SDK docs are public (no auth) so integrators can read them without an account. */}
            <Route path="/sdk-docs" element={<SDKDocumentation />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
          <div className="app-shell">
            <CommandPalette user={user} open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
            <Sidebar
              onLogout={handleLogout}
              user={user}
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
            />

            <div className="app-workspace">
              <TopNav
                user={user}
                onSearchClick={() => setCommandPaletteOpen(true)}
                onMenuClick={() => setSidebarOpen(true)}
              />

              <main className="portal-main">
                <Routes>
                  <Route path="/" element={user?.role === 'SUPER_ADMIN' ? <SuperAdminDashboard /> : <Dashboard user={user} />} />
                  <Route path="/dashboard" element={user?.role === 'SUPER_ADMIN' ? <SuperAdminDashboard /> : <Dashboard user={user} />} />
                  <Route path="/super-admin" element={<SuperAdminDashboard />} />
                  <Route path="/downloads" element={<Downloads />} />
                  <Route path="/sdk-docs" element={<SDKDocumentation />} />
                  <Route path="/licenses" element={<Licenses user={user} />} />
                  <Route path="/policies" element={<Policies />} />
                  <Route path="/users" element={<Users user={user} />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/customers" element={<Customers />} />
                  <Route path="/devices" element={<Devices />} />
                  <Route path="/partners" element={<Partners />} />
                  <Route path="/purchase-orders" element={<PurchaseOrders />} />
                  <Route path="/system-signatures" element={<SystemSignatures />} />
                  <Route path="/system-logs" element={<SystemLogs />} />
                  <Route path="/roles" element={<Roles />} />
                  <Route path="/organizations" element={<Organizations />} />
                  <Route path="/api-keys" element={<ApiKeys user={user} />} />
                  <Route path="/activations" element={<Activations />} />
                  <Route path="/verification" element={<VerificationPackages />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/activity-logs" element={<ActivityLogs />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/domains" element={<Domains />} />
                  <Route path="/webhooks" element={<Webhooks />} />
                  <Route path="/billing" element={<Billing user={user} />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/mfa" element={<MFA />} />
                  <Route path="/login" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </main>
            </div>
          </div>
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App;
